import { Box } from "@mui/system";
import {
    DataGridPro,
    GridColumnVisibilityModel,
    GridFilterModel,
    GridSortModel,
} from "@mui/x-data-grid-pro";
import { useMemo, useState, useCallback, useRef } from "react";

import {
    useDisableActiveModeMutation,
    useDisableKoBiddingAutoMaxCpcMutation,
    useDisableKoBiddingManualMaxCpcMutation,
    useUpdateKoBiddingManualMutation,
} from "src/context/api/activations/api";

import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useAuth } from "src/auth";

import ModalActivationsAuto from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-auto";
import ModalActivationsManual from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-manual";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import ModalCreateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy";
import ModalDeactivateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-deactivate-strategy";
import EnforceMaxCpcModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-enforce-max-cpc";

import { FilterPanelCustom } from "../filter-panel-custom";
import { useModals } from "./hooks/useModals";
import { dateRangeOptions, comparisonPeriodOptions } from "./constants/dateOptions";
import CustomToolbar from "./components/CustomToolbar";
import { useActions } from "./hooks/data-grid-keyword/useActions";
import { gridStyles } from "./styles/grid";
import { useKeywordExactColumns } from "./utils/useKeywordExactColumns";
import { useEnforceMaxCPCMutation } from "src/context/api/liveBidding/api";

interface Props {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortModel: GridSortModel;
    setSortModel: (model: GridSortModel) => void;
    filterModel: GridFilterModel;
    setFilterModel: (model: GridFilterModel) => void;
    localPatches: Record<string, any>;
    setLocalPatches: React.Dispatch<
        React.SetStateAction<Record<string, any>>
    >;
    selectedDateRange?: {
        label: string;
        value: string;
    };
    onDateRangeChange?: (newRange: {
        label: string;
        value: string;
    }) => void;
    selectedCompareDate?: any;
    onCompareDateChange?: (newRange: {
        label: string;
        value: string;
    }) => void;
    selectedCountry: string;
    onOpenCsvImport: () => void;
    refetch: any;
}

export default function DataGridKeywordExact({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    onDateRangeChange,
    onCompareDateChange,
    selectedCompareDate,
    selectedDateRange,
    onOpenCsvImport,
    refetch,
}: Props) {
    const [selectionRows, setSelectionRows]: any = useState([]);
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem("i18nextLng") || "en";
    const apiRef: any = useRef();
    const { user } = useAuth();
    const toolbarRef = useRef<HTMLDivElement>(null);

    const handleCloseActivationManual = useCallback(
        () =>
            setModalActivationManual({
                open: false,
                row: null,
            }),
        []
    );

    const handleCloseActivationAuto = useCallback(
        () =>
            setModalActivationAuto({
                open: false,
                row: null,
            }),
        []
    );

    const [enforceMaxCPC] = useEnforceMaxCPCMutation();
    const [disableActiveMode] = useDisableActiveModeMutation();
    const [disableKoBiddingManualMaxCpc] =
        useDisableKoBiddingManualMaxCpcMutation();
    const [disableKoBiddingAutoMaxCpc] =
        useDisableKoBiddingAutoMaxCpcMutation();
    const [updateMaxCpc] = useUpdateKoBiddingManualMutation();

    const {
        modalActivationManual,
        setModalActivationManual,
        modalActivationAuto,
        setModalActivationAuto,
        modalCreateStrategy,
        setModalCreateStrategy,
        modalDeactivateStrategy,
        setModalDeactivateStrategy,
        confirmDisable,
        setConfirmDisable,
        openEnforceModal,
        setOpenEnforceModal,
    } = useModals();

    const [modifiedRows, setModifiedRows] = useState<
        {
            id: string | number;
            max_cpc: number;
        }[]
    >([]);

    const {
        handleConfirmDisable,
        handleToggle,
        handleUpdateMaxCPC,
        handleFinish,
        handleRowUpdate,
    } = useActions({
        workspace,
        confirmDisable,
        enforceMaxCPC,
        updateMaxCpc,
        disableActiveMode,
        disableKoBiddingManualMaxCpc,
        disableKoBiddingAutoMaxCpc,
        setLocalPatches,
        setModifiedRows,
        setConfirmDisable,
    });

    const mergedRows = useMemo(() => {
        return data.map((row) => {
            const patch = localPatches[row.id];

            if (!patch) {
                return row;
            }

            return {
                ...row,
                ...patch,
                activations_details:
                    patch.activations_details ??
                    row.activations_details,
            };
        });
    }, [data, localPatches]);

    const columns = useKeywordExactColumns({
        workspace,
        lang,
        handleToggle,
        selectedCompareDate,
        setModalActivationManual,
        setModalActivationAuto,
        setConfirmDisable,
        setModalCreateStrategy,
    });

    const [
        columnVisibilityModel,
        setColumnVisibilityModel,
    ] = useState<GridColumnVisibilityModel>(() => {
        const saved = localStorage.getItem(
            "columnVisibilityModelActivationsExact"
        );

        return saved
            ? JSON.parse(saved)
            : {
                campaign_names: false,
                campaign_ids: false,
                ad_group_names: false,
                ad_group_ids: false,
                keyword_id: false,
                region_name: false,
                avg_cpc: false,
                cost_last30d: false,
                platform: false,
            };
    });

    const handleColumnVisibilityChange = useCallback(
        (newModel: GridColumnVisibilityModel) => {
            setColumnVisibilityModel(newModel);

            localStorage.setItem(
                "columnVisibilityModelActivationsExact",
                JSON.stringify(newModel)
            );
        },
        []
    );

    return (
        <Box sx={gridStyles}>
            <DataGridPro
                rowHeight={57}
                processRowUpdate={handleRowUpdate}
                apiRef={apiRef}
                getRowId={(r) => r.id}
                rows={mergedRows}
                rowCount={rowCount}
                paginationModel={{
                    page,
                    pageSize,
                }}
                pageSizeOptions={[
                    10,
                    25,
                    50,
                    100,
                    200,
                ]}
                columnVisibilityModel={
                    columnVisibilityModel
                }
                onColumnVisibilityModelChange={
                    handleColumnVisibilityChange
                }
                onPaginationModelChange={({
                    page,
                    pageSize,
                }) => {
                    onPageChange(page);
                    onPageSizeChange(pageSize);
                }}
                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                keepNonExistentRowsSelected
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                slots={{
                    toolbar: () => (
                        <CustomToolbar
                            disableScraping
                            toolbarRef={toolbarRef}
                            apiRef={apiRef}
                            selectionRows={
                                selectionRows
                            }
                            canImport={[
                                "admin",
                                "brand specialist",
                            ].includes(
                                user?.role ?? ""
                            )}
                            onOpenCsvImport={
                                onOpenCsvImport
                            }
                            selectedDateRange={
                                selectedDateRange
                            }
                            selectedCompareDate={
                                selectedCompareDate
                            }
                            onDateRangeChange={
                                onDateRangeChange
                            }
                            onCompareDateChange={
                                onCompareDateChange
                            }
                            modifiedRows={
                                modifiedRows
                            }
                            setModifiedRows={
                                setModifiedRows
                            }
                            setLocalPatches={
                                setLocalPatches
                            }
                            handleUpdateMaxCPC={
                                handleUpdateMaxCPC
                            }
                            setModalDeactivateStrategy={
                                setModalDeactivateStrategy
                            }
                            setModalCreateStrategy={
                                setModalCreateStrategy
                            }
                            setOpenEnforceModal={
                                setOpenEnforceModal
                            }
                            dateRangeOptions={
                                dateRangeOptions
                            }
                            comparisonPeriodOptions={
                                comparisonPeriodOptions
                            }
                        />
                    ),
                    filterPanel: (props) => (
                        <FilterPanelCustom
                            {...props}
                            getColumnForNewFilter={(
                                args
                            ) =>
                                args
                                    ? args.columns[0]
                                        .field
                                    : undefined
                            }
                        />
                    ),
                }}
                checkboxSelection
                onRowSelectionModelChange={(
                    newSelection: any
                ) => {
                    const selectedIds =
                        Array.isArray(
                            newSelection
                        )
                            ? newSelection
                            : [];

                    setSelectionRows(
                        mergedRows.filter((row) =>
                            selectedIds.includes(
                                row.id
                            )
                        )
                    );
                }}
                columnHeaderHeight={50}
                getRowClassName={(params) =>
                    [
                        params.row.isEdited
                            ? "edited-row"
                            : "",
                        params.row.isInvalid
                            ? "row-invalid"
                            : "",
                    ].join(" ")
                }
                isCellEditable={(params) => {
                    if (
                        params.field !==
                        "max_cpc"
                    ) {
                        return false;
                    }

                    const activations =
                        params.row
                            .activations_details ||
                        [];

                    const koManual =
                        activations.find(
                            (d: any) =>
                                d.status ===
                                true &&
                                d.mode ===
                                "KO Bidding Manual"
                        );

                    return Boolean(koManual);
                }}
                disableRowSelectionOnClick
                columns={columns}
            />

            {modalActivationManual.open && (
                <ModalActivationsManual
                    open={
                        modalActivationManual.open
                    }
                    row={
                        modalActivationManual.row
                    }
                    onClose={
                        handleCloseActivationManual
                    }
                    setLocalPatches={
                        setLocalPatches
                    }
                />
            )}

            {modalActivationAuto.open && (
                <ModalActivationsAuto
                    open={
                        modalActivationAuto.open
                    }
                    row={
                        modalActivationAuto.row
                    }
                    onClose={
                        handleCloseActivationAuto
                    }
                    setLocalPatches={
                        setLocalPatches
                    }
                    tab="keyword"
                />
            )}

            <ModalCreateStrategy
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() =>
                    setModalCreateStrategy({
                        open: false,
                        row: null,
                    })
                }
                setLocalPatches={
                    setLocalPatches
                }
            />

            <ModalDeactivateStrategy
                refetch={refetch}
                open={
                    modalDeactivateStrategy.open
                }
                row={modalDeactivateStrategy.row}
                onClose={() =>
                    setModalDeactivateStrategy({
                        open: false,
                        row: null,
                    })
                }
                setLocalPatches={
                    setLocalPatches
                }
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() =>
                    setConfirmDisable({
                        open: false,
                        row: null,
                        mode: null,
                    })
                }
                onConfirm={
                    handleConfirmDisable
                }
            />

            <EnforceMaxCpcModal
                workspaceId={workspace?.id}
                onFinish={handleFinish}
                open={openEnforceModal.open}
                onClose={() =>
                    setOpenEnforceModal({
                        open: false,
                        row: null,
                    })
                }
                selectedRows={
                    openEnforceModal.row
                }
            />
        </Box>
    );
}